import React, { useRef, useState, useCallback } from 'react';
import { Sun, Maximize2, RotateCcw } from 'lucide-react';

// Preview dimensions (represents a slice of the top of the real page)
const PREVIEW_W = 600;
const PREVIEW_H = 400;

// sun_y range in real page (px): -200 → +200 maps to preview y: 0 → PREVIEW_H
const SUN_Y_MIN = -200;
const SUN_Y_MAX = 200;

const SunEditor = ({
  sunX = 50,
  sunY = -90,
  sunScale = 1,
  sunBrightness = 1,
  onChange,
}) => {
  const previewRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // Map real coords → preview coords (px inside PREVIEW_W × PREVIEW_H)
  const previewX = (sunX / 100) * PREVIEW_W;
  const previewY = ((sunY - SUN_Y_MIN) / (SUN_Y_MAX - SUN_Y_MIN)) * PREVIEW_H;

  const getPreviewPoint = useCallback((e) => {
    const rect = previewRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    // Normalize by displayed size back to virtual PREVIEW_W × PREVIEW_H
    const px = ((clientX - rect.left) / rect.width) * PREVIEW_W;
    const py = ((clientY - rect.top) / rect.height) * PREVIEW_H;
    return { px, py };
  }, []);

  const updateFromPreview = useCallback((px, py) => {
    const clampedX = Math.max(0, Math.min(PREVIEW_W, px));
    const clampedY = Math.max(0, Math.min(PREVIEW_H, py));
    const newSunX = Math.round((clampedX / PREVIEW_W) * 100 * 10) / 10; // %
    const newSunY = Math.round(SUN_Y_MIN + (clampedY / PREVIEW_H) * (SUN_Y_MAX - SUN_Y_MIN));
    onChange({ sun_x: newSunX, sun_y: newSunY });
  }, [onChange]);

  const handlePointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    const p = getPreviewPoint(e);
    if (p) updateFromPreview(p.px, p.py);
  };

  const handlePointerMove = useCallback((e) => {
    if (!dragging) return;
    e.preventDefault();
    const p = getPreviewPoint(e);
    if (p) updateFromPreview(p.px, p.py);
  }, [dragging, getPreviewPoint, updateFromPreview]);

  const handlePointerUp = useCallback(() => setDragging(false), []);

  const handleReset = () => {
    onChange({ sun_x: 50, sun_y: -90, sun_scale: 1, sun_brightness: 1 });
  };

  // Size of the sun preview dot — scaled to match real sun rendering
  const sunPreviewSize = 110 * sunScale; // matches .sun-core base size

  return (
    <div className="admin-section" data-testid="sun-editor-section">
      <h3 className="admin-section-title flex items-center gap-2">
        <Sun size={18} className="text-yellow-400" /> Günəş effekti
      </h3>
      <p className="admin-section-desc">
        Günəşi sürükləyib yerini dəyişdirin. Ölçü və işıq parlaqlığını slider-lərlə tənzimləyin.
      </p>

      {/* Drag preview area */}
      <div
        ref={previewRef}
        className="sun-preview-area"
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        style={{ cursor: dragging ? 'grabbing' : 'grab' }}
        data-testid="sun-preview-area"
      >
        {/* Viewport top marker */}
        <div className="sun-preview-marker" style={{ top: ((-SUN_Y_MIN) / (SUN_Y_MAX - SUN_Y_MIN)) * 100 + '%' }}>
          <span>↑ Səhifənin yuxarı kənarı</span>
        </div>

        {/* Preview sun */}
        <div
          className="sun-preview-sun"
          style={{
            left: `${(previewX / PREVIEW_W) * 100}%`,
            top: `${(previewY / PREVIEW_H) * 100}%`,
            width: `${sunPreviewSize}px`,
            height: `${sunPreviewSize}px`,
            marginLeft: `${-sunPreviewSize / 2}px`,
            marginTop: `${-sunPreviewSize / 2}px`,
            filter: `brightness(${sunBrightness})`,
          }}
          data-testid="sun-preview-sun"
        />

        {/* Coordinates readout */}
        <div className="sun-preview-coords">
          X: {sunX}%  ·  Y: {sunY}px
        </div>
      </div>

      <div className="sun-controls-grid">
        {/* Scale */}
        <div className="sun-control">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-white/70 flex items-center gap-1">
              <Maximize2 size={12} /> Ölçü
            </span>
            <span className="font-mono text-yellow-300" data-testid="sun-scale-value">
              {sunScale.toFixed(2)}x
            </span>
          </div>
          <input
            type="range"
            min="0.2" max="3" step="0.05"
            value={sunScale}
            onChange={(e) => onChange({ sun_scale: parseFloat(e.target.value) })}
            className="size-slider"
            data-testid="sun-scale-slider"
          />
        </div>

        {/* Brightness */}
        <div className="sun-control">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-white/70 flex items-center gap-1">
              <Sun size={12} /> İşıq (parlaqlıq)
            </span>
            <span className="font-mono text-yellow-300" data-testid="sun-brightness-value">
              {sunBrightness.toFixed(2)}x
            </span>
          </div>
          <input
            type="range"
            min="0.2" max="3" step="0.05"
            value={sunBrightness}
            onChange={(e) => onChange({ sun_brightness: parseFloat(e.target.value) })}
            className="size-slider"
            data-testid="sun-brightness-slider"
          />
        </div>
      </div>

      <button
        onClick={handleReset}
        className="admin-btn-secondary mt-4"
        data-testid="sun-reset-btn"
      >
        <RotateCcw size={14} /> Defolt mövqeyə qaytar
      </button>
    </div>
  );
};

export default SunEditor;
